import { useEffect, useMemo, useState } from 'react';
import { getLogoDataUrl } from '@/utils/logoDataUrl';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  FileDownload as DownloadIcon,
  People as PeopleIcon,
  Inventory2 as ProductIcon,
  LocalShipping as DeliveryIcon,
  Print as PrintIcon,
  TrendingUp as TrendIcon,
  CalendarToday as CalIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';
import { ordersApi } from '@/api/orders';
import { tenantApi } from '@/api/tenant';
import { PageHeader } from '@/components';
import type { Order } from '@/types/order';
import type { PeerTenant } from '@/types';
import { downloadExcelFromBackend } from '@/utils/excelExport';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtINR(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}L`;
  if (n >= 1000) return `₹${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: 1 })}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtINRFull(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function orderValue(o: Order) {
  return (o.items ?? []).reduce((s, i) => s + Number(i.unitPrice) * Number(i.qty), 0);
}

function inRange(dateStr: string, from: Date, to: Date): boolean {
  const d = new Date(dateStr);
  return d >= from && d <= to;
}

function toDate(str: string) {
  const d = new Date(str); d.setHours(23, 59, 59, 999); return d;
}
function fromDate(str: string) {
  const d = new Date(str); d.setHours(0, 0, 0, 0); return d;
}

// ---------------------------------------------------------------------------
// Status palette
// ---------------------------------------------------------------------------
const STATUS_COLOR: Record<string, string> = {
  PLACED: '#1565c0', VIEWED: '#6a1b9a', CONFIRMED: '#00897b',
  PARTIALLY_FULFILLED: '#ef6c00', DISPATCHED: '#0277bd',
  DELIVERED: '#2e7d32', INVOICED: '#558b2f',
  CANCELLED: '#c62828', REJECTED: '#b71c1c',
};

// ---------------------------------------------------------------------------
// SVG Bar chart — fixed viewBox so text is never distorted
// ---------------------------------------------------------------------------
function BarChart({ data, color }: { data: { label: string; value: number }[]; color: string }) {
  if (data.length === 0) return null;
  const n = data.length;
  // 12px per bar keeps the chart height reasonable even at 180 days
  const VW = Math.max(n * 12, 500);
  const VH = 260;
  const padL = 52, padR = 12, padT = 32, padB = 48;
  const chartW = VW - padL - padR;
  const chartH = VH - padT - padB;
  const max = Math.max(...data.map(d => d.value), 1);
  const gap = chartW / n;
  const barW = Math.max(gap * 0.6, 4);

  // Show value labels only for top 8 bars — avoids all overlap
  const top8idx = new Set(
    data.map((d, i) => ({ i, v: d.value }))
      .filter(x => x.v > 0)
      .sort((a, b) => b.v - a.v)
      .slice(0, 8)
      .map(x => x.i)
  );
  // X-axis: label every Nth bar
  const xStep = Math.max(1, Math.floor(n / 10));
  const yTicks = [0.25, 0.5, 0.75, 1].map(r => ({ r, v: max * r, y: padT + chartH * (1 - r) }));

  return (
    // Aspect-ratio wrapper — SVG fills it exactly, so all coordinates map 1:1 to pixels
    <Box sx={{ position: 'relative', width: '100%', paddingBottom: `${(VH / VW) * 100}%`, overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        {/* Y-axis grid + labels */}
        {yTicks.map(t => (
          <g key={t.r}>
            <line x1={padL} y1={t.y} x2={VW - padR} y2={t.y}
              stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4,3" />
            <text x={padL - 6} y={t.y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
              {fmtINR(t.v)}
            </text>
          </g>
        ))}
        {/* Bars */}
        {data.map((d, i) => {
          const bh = Math.max((d.value / max) * chartH, d.value > 0 ? 3 : 0);
          const cx = padL + i * gap + gap / 2;
          const bx = cx - barW / 2;
          const by = padT + chartH - bh;
          return (
            <g key={i}>
              <rect x={bx} y={by} width={barW} height={bh} fill={color} opacity={0.82} rx={2} />
              {/* Value label: only top 8 bars — white halo ensures contrast */}
              {top8idx.has(i) && (
                <text
                  x={cx} y={Math.max(by - 6, padT + 4)}
                  textAnchor="middle" fontSize="11"
                  fill={color} stroke="white" strokeWidth="3" paintOrder="stroke"
                >
                  {fmtINR(d.value)}
                </text>
              )}
              {/* X-axis label every xStep */}
              {i % xStep === 0 && (
                <text x={cx} y={padT + chartH + 14} textAnchor="end" fontSize="10" fill="#4b5563"
                  transform={`rotate(-35,${cx},${padT + chartH + 14})`}>
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const W = 100, H = 40, pad = 2;
  const pts = data.map((v, i) => ({
    x: pad + (i / (data.length - 1)) * (W - pad * 2),
    y: pad + (1 - v / max) * (H - pad * 2),
  }));
  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${pts[pts.length - 1].x},${H} L${pts[0].x},${H} Z`;
  const gid = `sp-${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gid})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Download helper
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Metric card
// ---------------------------------------------------------------------------
function MetricCard({ label, value, sub, color, spark }: { label: string; value: string; sub?: string; color: string; spark?: number[] }) {
  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1.5px solid ${color}22` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: spark ? 1 : 2.5 } }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>{label}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.5 }}>{sub}</Typography>}
        {spark && <Box sx={{ mt: 1 }}><Sparkline data={spark} color={color} /></Box>}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rank badge
// ---------------------------------------------------------------------------
function Rank({ n }: { n: number }) {
  const bg = n === 1 ? '#f59e0b' : n === 2 ? '#94a3b8' : n === 3 ? '#cd7c3d' : '#e5e7eb';
  const fg = n <= 3 ? '#fff' : '#374151';
  return (
    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: fg }}>{n}</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Share bar cell
// ---------------------------------------------------------------------------
function ShareBar({ pct, color }: { pct: number; color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#f3f4f6', overflow: 'hidden', minWidth: 60 }}>
        <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`, bgcolor: color, borderRadius: 3 }} />
      </Box>
      <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{pct.toFixed(1)}%</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid #f3f4f6' }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#152346' }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>{sub}</Typography>}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Common table styles
// ---------------------------------------------------------------------------
const thSx = { fontWeight: 700, fontSize: 12, color: '#374151', bgcolor: '#f8fafc', py: 1.25, px: 2 };
const tdSx = { fontSize: 13, py: 1.25, px: 2, borderBottom: '1px solid #f3f4f6' };

function defaultFrom() {
  const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10);
}
function defaultTo() { return new Date().toISOString().slice(0, 10); }

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function ReportsPage() {
  const { user } = useAuthStore();
  const isDistributor = user?.tenantType === 'WHOLESALER';

  // For retailers and representatives the metric is "order value" (they purchase,
  // not sell), whereas distributors/wholesalers track "revenue".
  const rv = isDistributor ? 'Revenue' : 'Order Value';
  const rvLow = rv.toLowerCase();

  const REPORTS = [
    { id: 'overview', icon: <AssessmentIcon />, title: `${rv} Overview`, description: `Total ${rvLow}, order count and daily trend`, accent: '#1565c0' },
    { id: 'customers', icon: <PeopleIcon />,
      title: isDistributor ? 'Top Customers' : 'Top Suppliers',
      description: isDistributor
        ? `Highest spending customers ranked by ${rvLow}`
        : `Suppliers ranked by total ${rvLow} in the period`,
      accent: '#00897b' },
    { id: 'products', icon: <ProductIcon />, title: `${rv} by Product`, description: `Products ranked by units ${isDistributor ? 'sold' : 'ordered'} and ${rvLow}`, accent: '#6a1b9a' },
    { id: 'status', icon: <BarChartIcon />, title: 'Order Status Breakdown', description: 'Distribution across all order statuses', accent: '#ef6c00' },
    { id: 'trend', icon: <TrendIcon />, title: `Daily ${rv} Trend`, description: `Day-by-day ${rvLow} chart and top days table`, accent: '#c62828' },
    { id: 'delivery', icon: <DeliveryIcon />, title: 'Fulfilment Report', description: 'Confirmed vs delivered vs cancelled rates', accent: '#0277bd' },
    ...(isDistributor ? [{ id: 'reps', icon: <PeopleIcon />, title: 'Rep Orders', description: 'Orders placed by sales representatives with total value', accent: '#7B61FF' }] : []),
  ];

  const [fromStr, setFromStr] = useState(defaultFrom);
  const [toStr, setToStr] = useState(defaultTo);
  const [activeReport, setActiveReport] = useState('overview');
  const [orders, setOrders] = useState<Order[]>([]);
  const [peers, setPeers] = useState<PeerTenant[]>([]);
  const [reps, setReps] = useState<PeerTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRepIds, setExpandedRepIds] = useState<Set<number | string>>(new Set());
  const [generateAnchor, setGenerateAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const ordersPromise = isDistributor
      ? ordersApi.list({ distributorId: user.tenantId, size: 2000 })
      : ordersApi.list({ retailerId: user.tenantId, size: 2000 });
    Promise.allSettled([
      ordersPromise,
      tenantApi.listPeers(undefined, 0, 1000),
      tenantApi.listReps(undefined, 0, 500),
    ])
      .then(([ordRes, peerRes, repsRes]) => {
        if (ordRes.status === 'fulfilled') setOrders(ordRes.value.content ?? []);
        if (peerRes.status === 'fulfilled') setPeers(peerRes.value.content ?? []);
        if (repsRes.status === 'fulfilled') setReps(repsRes.value.content ?? []);
      }).finally(() => setLoading(false));
  }, [user, isDistributor]);

  const peerMap = useMemo(() => {
    const m: Record<number, string> = {};
    peers.forEach(p => { m[p.id] = p.name; });
    return m;
  }, [peers]);

  const repsMap = useMemo(() => {
    const m = new Map<number, string>();
    reps.forEach(r => m.set(r.id, r.name));
    return m;
  }, [reps]);

  // Rep orders: group by rep (salesRepTenantId or salesRepUserId used as key)
  const repOrderRows = useMemo(() => {
    if (!isDistributor) return [];
    const from = fromDate(fromStr), to = toDate(toStr);
    const repOrders = orders.filter(o =>
      (o.salesRepTenantId ?? o.salesRepUserId) &&
      o.orderDate && inRange(o.orderDate, from, to),
    );
    const groupMap = new Map<number | string, { key: number | string; name: string; orders: Order[] }>();
    for (const o of repOrders) {
      const key = o.salesRepTenantId ?? o.salesRepUserId ?? 0;
      const name = o.salesRepName
        ?? (o.salesRepTenantId ? repsMap.get(o.salesRepTenantId) : undefined)
        ?? (o.salesRepUserId ? `Rep #${o.salesRepUserId}` : 'Unknown Rep');
      if (!groupMap.has(key)) groupMap.set(key, { key, name, orders: [] });
      groupMap.get(key)!.orders.push(o);
    }
    return Array.from(groupMap.values())
      .sort((a, b) => {
        const va = a.orders.reduce((s, o) => s + orderValue(o), 0);
        const vb = b.orders.reduce((s, o) => s + orderValue(o), 0);
        return vb - va;
      });
  }, [orders, fromStr, toStr, isDistributor, repsMap]);

  const filtered = useMemo(() => {
    const from = fromDate(fromStr), to = toDate(toStr);
    return orders.filter(o => o.orderDate && inRange(o.orderDate, from, to));
  }, [orders, fromStr, toStr]);

  const totalRevenue = useMemo(() => filtered.reduce((s, o) => s + orderValue(o), 0), [filtered]);
  const totalOrders = filtered.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const completedRevenue = useMemo(() =>
    filtered.filter(o => o.status === 'DELIVERED' || o.status === 'INVOICED')
      .reduce((s, o) => s + orderValue(o), 0), [filtered]);

  const dailySpark = useMemo(() => {
    const from = fromDate(fromStr), to = toDate(toStr);
    const days = Math.min(Math.ceil((to.getTime() - from.getTime()) / 86400000) + 1, 60);
    const arr = new Array(days).fill(0);
    for (const o of filtered) {
      const diff = Math.floor((new Date(o.orderDate).getTime() - from.getTime()) / 86400000);
      if (diff >= 0 && diff < days) arr[diff] += orderValue(o);
    }
    return arr;
  }, [filtered, fromStr, toStr]);

  const topCustomers = useMemo(() => {
    const map: Record<number, { name: string; revenue: number; count: number }> = {};
    for (const o of filtered) {
      const id = isDistributor ? o.retailerTenantId : o.distributorTenantId;
      const name = peerMap[id] ?? `Tenant #${id}`;
      if (!map[id]) map[id] = { name, revenue: 0, count: 0 };
      map[id].revenue += orderValue(o); map[id].count++;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 20);
  }, [filtered, peerMap, isDistributor]);

  const topProducts = useMemo(() => {
    const map: Record<string, { name: string; sku: string; qty: number; revenue: number }> = {};
    for (const o of filtered) {
      for (const item of o.items ?? []) {
        const k = String(item.productId);
        if (!map[k]) map[k] = { name: item.productName, sku: item.sku, qty: 0, revenue: 0 };
        map[k].qty += Number(item.qty);
        map[k].revenue += Number(item.unitPrice) * Number(item.qty);
      }
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 20);
  }, [filtered]);

  const statusBreakdown = useMemo(() => {
    const map: Record<string, { count: number; revenue: number }> = {};
    for (const o of filtered) {
      if (!map[o.status]) map[o.status] = { count: 0, revenue: 0 };
      map[o.status].count++; map[o.status].revenue += orderValue(o);
    }
    return Object.entries(map).map(([status, v]) => ({ status, ...v })).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);

  const dailyTrend = useMemo(() => {
    const from = fromDate(fromStr), to = toDate(toStr);
    const days = Math.min(Math.ceil((to.getTime() - from.getTime()) / 86400000) + 1, 180);
    const result: { label: string; value: number; date: Date }[] = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(from.getTime() + i * 86400000);
      result.push({ label: `${d.getDate()}/${d.getMonth() + 1}`, value: 0, date: d });
    }
    for (const o of filtered) {
      const diff = Math.floor((new Date(o.orderDate).getTime() - from.getTime()) / 86400000);
      if (diff >= 0 && diff < days) result[diff].value += orderValue(o);
    }
    return result;
  }, [filtered, fromStr, toStr]);

  const fulfilmentStats = useMemo(() => {
    const total = filtered.length;
    return {
      total,
      delivered: filtered.filter(o => o.status === 'DELIVERED' || o.status === 'INVOICED').length,
      confirmed: filtered.filter(o => ['CONFIRMED', 'PARTIALLY_FULFILLED', 'DISPATCHED'].includes(o.status)).length,
      cancelled: filtered.filter(o => o.status === 'CANCELLED' || o.status === 'REJECTED').length,
      pending: filtered.filter(o => o.status === 'PLACED' || o.status === 'VIEWED').length,
    };
  }, [filtered]);

  function downloadExcel() {
    const rep = REPORTS.find(r => r.id === activeReport)!;
    let headers: string[], rows: (string | number)[][];
    if (activeReport === 'overview') {
      headers = ['Metric', 'Value'];
      rows = [
        [`Total ${rv}`, fmtINRFull(totalRevenue)],
        ['Total Orders', totalOrders],
        ['Avg Order Value', fmtINRFull(avgOrderValue)],
        [`Completed ${rv}`, fmtINRFull(completedRevenue)],
      ];
    } else if (activeReport === 'customers') {
      headers = ['#', 'Name', 'Orders', rv, 'Avg Order', 'Share %'];
      rows = topCustomers.map((c, i) => [i + 1, c.name, c.count, fmtINRFull(c.revenue), fmtINRFull(c.revenue / c.count), totalRevenue > 0 ? ((c.revenue / totalRevenue) * 100).toFixed(1) + '%' : '0%']);
    } else if (activeReport === 'products') {
      headers = ['#', 'Product', 'SKU', isDistributor ? 'Qty Sold' : 'Qty Ordered', rv, 'Share %'];
      rows = topProducts.map((p, i) => [i + 1, p.name, p.sku, p.qty, fmtINRFull(p.revenue), totalRevenue > 0 ? ((p.revenue / totalRevenue) * 100).toFixed(1) + '%' : '0%']);
    } else if (activeReport === 'status') {
      headers = ['Status', 'Orders', rv, 'Share %'];
      rows = statusBreakdown.map(s => [s.status, s.count, fmtINRFull(s.revenue), totalRevenue > 0 ? ((s.revenue / totalRevenue) * 100).toFixed(1) + '%' : '0%']);
    } else if (activeReport === 'trend') {
      headers = ['Date', rv];
      rows = dailyTrend.filter(d => d.value > 0).sort((a, b) => b.value - a.value).slice(0, 30).map(d => [d.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), fmtINRFull(d.value)]);
    } else if (activeReport === 'delivery') {
      headers = ['Category', 'Orders', '% of Total'];
      const { total, delivered, confirmed, cancelled, pending } = fulfilmentStats;
      rows = [
        ['Delivered / Invoiced', delivered, total > 0 ? ((delivered / total) * 100).toFixed(1) + '%' : '0%'],
        ['In Progress', confirmed, total > 0 ? ((confirmed / total) * 100).toFixed(1) + '%' : '0%'],
        ['Pending Review', pending, total > 0 ? ((pending / total) * 100).toFixed(1) + '%' : '0%'],
        ['Cancelled / Rejected', cancelled, total > 0 ? ((cancelled / total) * 100).toFixed(1) + '%' : '0%'],
        ['Total', total, '100%'],
      ];
    } else {
      headers = ['Rep', 'Orders', rv];
      rows = repOrderRows.map(r => [r.name, r.orders.length, fmtINRFull(r.orders.reduce((s, o) => s + orderValue(o), 0))]);
    }
    const userName = user?.tenantName ?? '';
    downloadExcelFromBackend({
      title: rep.title ?? rep.id,
      subTitle: `${userName ? `${userName}   |   ` : ''}Generated: ${new Date().toLocaleDateString('en-IN')}`,
      headers,
      rows,
    });
  }

  async function printReport() {
    const rep = REPORTS.find(r => r.id === activeReport)!;
    const logoDataUrl = await getLogoDataUrl().catch(() => null);
    const userName = user?.tenantName ?? '';
    const genDate = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    const logoTag = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Ardinotech" style="height:44px;max-width:200px;object-fit:contain;" />`
      : `<span style="font-size:18px;font-weight:800;color:#fff;">Ardinotech</span>`;
    const headerHtml = `<div style="background:#152346;padding:12px 20px;display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;border-radius:4px;">${logoTag}<div style="text-align:right;color:#fff;"><div style="font-size:13px;font-weight:600;">${userName ? `Name: ${userName}` : ''}</div><div style="font-size:11px;opacity:0.8;">Generated: ${genDate}</div></div></div>`;
    const style = `body{font-family:sans-serif;font-size:13px;color:#000;margin:24px}h3{margin:0 0 4px;color:#000}p{margin:0 0 14px;color:#374151;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #e5e7eb;padding:5px 9px;color:#000}th{background:#f3f4f6;font-weight:700;font-size:11px;text-transform:uppercase}tr:nth-child(even){background:#fafafa}@media print{div[style*="background:#152346"]{-webkit-print-color-adjust:exact;print-color-adjust:exact}}`;
    let tableHtml = '';
    if (activeReport === 'customers') {
      tableHtml = `<table><thead><tr><th>#</th><th>Name</th><th>Orders</th><th>${rv}</th><th>Avg Order</th><th>Share</th></tr></thead><tbody>${topCustomers.map((c, i) => `<tr><td>${i+1}</td><td>${c.name}</td><td>${c.count}</td><td>${fmtINRFull(c.revenue)}</td><td>${fmtINRFull(c.revenue/c.count)}</td><td>${totalRevenue>0?((c.revenue/totalRevenue)*100).toFixed(1):0}%</td></tr>`).join('')}</tbody></table>`;
    } else if (activeReport === 'products') {
      tableHtml = `<table><thead><tr><th>#</th><th>Product</th><th>SKU</th><th>Qty</th><th>${rv}</th><th>Share</th></tr></thead><tbody>${topProducts.map((p, i) => `<tr><td>${i+1}</td><td>${p.name}</td><td>${p.sku}</td><td>${p.qty}</td><td>${fmtINRFull(p.revenue)}</td><td>${totalRevenue>0?((p.revenue/totalRevenue)*100).toFixed(1):0}%</td></tr>`).join('')}</tbody></table>`;
    } else if (activeReport === 'status') {
      tableHtml = `<table><thead><tr><th>Status</th><th>Orders</th><th>${rv}</th><th>Share</th></tr></thead><tbody>${statusBreakdown.map(s=>`<tr><td>${s.status}</td><td>${s.count}</td><td>${fmtINRFull(s.revenue)}</td><td>${totalRevenue>0?((s.revenue/totalRevenue)*100).toFixed(1):0}%</td></tr>`).join('')}</tbody></table>`;
    } else if (activeReport === 'trend') {
      tableHtml = `<table><thead><tr><th>Date</th><th>${rv}</th></tr></thead><tbody>${dailyTrend.filter(d=>d.value>0).sort((a,b)=>b.value-a.value).slice(0,30).map(d=>`<tr><td>${d.date.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</td><td>${fmtINRFull(d.value)}</td></tr>`).join('')}</tbody></table>`;
    } else if (activeReport === 'delivery') {
      const { total, delivered, confirmed, cancelled, pending } = fulfilmentStats;
      tableHtml = `<table><thead><tr><th>Category</th><th>Orders</th><th>%</th></tr></thead><tbody><tr><td>Delivered/Invoiced</td><td>${delivered}</td><td>${total>0?((delivered/total)*100).toFixed(1):0}%</td></tr><tr><td>In Progress</td><td>${confirmed}</td><td>${total>0?((confirmed/total)*100).toFixed(1):0}%</td></tr><tr><td>Pending Review</td><td>${pending}</td><td>${total>0?((pending/total)*100).toFixed(1):0}%</td></tr><tr><td>Cancelled/Rejected</td><td>${cancelled}</td><td>${total>0?((cancelled/total)*100).toFixed(1):0}%</td></tr></tbody></table>`;
    } else if (activeReport === 'reps') {
      tableHtml = `<table><thead><tr><th>Rep</th><th>Orders</th><th>Total Value</th></tr></thead><tbody>${repOrderRows.map(r=>`<tr><td>${r.name}</td><td>${r.orders.length}</td><td>${fmtINRFull(r.orders.reduce((s,o)=>s+orderValue(o),0))}</td></tr>`).join('')}</tbody></table>`;
    } else {
      tableHtml = `<table><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody><tr><td>Total ${rv}</td><td>${fmtINRFull(totalRevenue)}</td></tr><tr><td>Total Orders</td><td>${totalOrders}</td></tr><tr><td>Avg Order Value</td><td>${fmtINRFull(avgOrderValue)}</td></tr><tr><td>Completed ${rv}</td><td>${fmtINRFull(completedRevenue)}</td></tr></tbody></table>`;
    }
    const html = `<!DOCTYPE html><html><head><title>${rep.title}</title><style>${style}</style></head><body>${headerHtml}<h3>${rep.title}</h3><p>${rep.description} &nbsp;|&nbsp; ${fromStr} to ${toStr}</p>${tableHtml}</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  }

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title="Reports"
        subtitle={`Analyse orders, ${rvLow}, ${isDistributor ? 'customers' : 'suppliers'} and products`}
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ fontSize: 13 }}>Report</InputLabel>
              <Select
                label="Report"
                value={activeReport}
                onChange={e => setActiveReport(e.target.value)}
                sx={{ fontSize: 13, bgcolor: '#fff' }}
              >
                {REPORTS.map(r => (
                  <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: r.accent, display: 'flex', alignItems: 'center' }}>{r.icon}</Box>
                      {r.title}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={e => setGenerateAnchor(e.currentTarget)}
              sx={{ fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Generate
            </Button>
            <Menu
              anchorEl={generateAnchor}
              open={Boolean(generateAnchor)}
              onClose={() => setGenerateAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 200, mt: 0.5 } }}
            >
              <MenuItem dense sx={{ fontSize: 13, gap: 1, py: 0.8 }} onClick={() => { downloadExcel(); setGenerateAnchor(null); }}>
                <DownloadIcon sx={{ fontSize: 16, color: '#15803d' }} /> Download Excel (.xls)
              </MenuItem>
              <MenuItem dense sx={{ fontSize: 13, gap: 1, py: 0.8 }} onClick={() => { printReport(); setGenerateAnchor(null); }}>
                <PrintIcon sx={{ fontSize: 16, color: '#dc2626' }} /> Print / PDF
              </MenuItem>
            </Menu>
          </Box>
        }
      />

      {/* ── Date range ── */}
      <Card sx={{ mb: 2.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
            <CalIcon sx={{ color: '#6b7280', fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Date Range</Typography>
            <TextField type="date" size="small" label="From" value={fromStr}
              onChange={e => setFromStr(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }} inputProps={{ max: toStr }} />
            <TextField type="date" size="small" label="To" value={toStr}
              onChange={e => setToStr(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }} inputProps={{ min: fromStr }} />
            <Chip label={`${filtered.length} orders`} size="small"
              sx={{ bgcolor: '#eff6ff', color: '#1565c0', fontWeight: 700, fontSize: 12, px: 0.5 }} />
            <Chip label={fmtINR(totalRevenue)} size="small"
              sx={{ bgcolor: '#f0fdf4', color: '#2e7d32', fontWeight: 700, fontSize: 12, px: 0.5 }} />
            {[{ label: 'Today', days: 0 }, { label: '7D', days: 6 }, { label: '30D', days: 29 }, { label: '90D', days: 89 }, { label: '180D', days: 179 }].map(p => (
              <Chip key={p.label} label={p.label} size="small" onClick={() => {
                const t = new Date().toISOString().slice(0, 10);
                const f = new Date(Date.now() - p.days * 86400000).toISOString().slice(0, 10);
                setFromStr(f); setToStr(t);
              }} sx={{ cursor: 'pointer', fontSize: 12, bgcolor: '#f9fafb', border: '1px solid #e5e7eb', '&:hover': { bgcolor: '#f3f4f6' } }} />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* ── Overview ── */}
      {activeReport === 'overview' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} md={3}><MetricCard label={`Total ${rv}`} value={fmtINR(totalRevenue)} sub={fmtINRFull(totalRevenue)} color="#1565c0" spark={dailySpark} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Total Orders" value={String(totalOrders)} sub="in selected period" color="#00897b" /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Avg Order Value" value={fmtINR(avgOrderValue)} sub="per order" color="#6a1b9a" /></Grid>
            <Grid item xs={6} md={3}><MetricCard label={`Completed ${rv}`} value={fmtINR(completedRevenue)} sub="Delivered + Invoiced" color="#2e7d32" /></Grid>
          </Grid>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <SectionHeader title={`Daily ${rv}`} sub={`${rv} per day in the selected period`} />
            <Box sx={{ p: 2 }}>
              <BarChart data={dailyTrend.filter((_, i) => i % Math.max(1, Math.floor(dailyTrend.length / 30)) === 0)} color="#1565c0" />
            </Box>
          </Card>
        </Box>
      )}

      {/* ── Top Customers / Suppliers ── */}
      {activeReport === 'customers' && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionHeader
            title={`Top ${isDistributor ? 'Customers' : 'Suppliers'} by ${rv}`}
            sub={`Ranked by total ${rvLow} in the selected period`}
          />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>#</TableCell>
                <TableCell sx={thSx}>Name</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Orders</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>{rv}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Avg Order</TableCell>
                <TableCell sx={{ ...thSx, minWidth: 160 }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topCustomers.map((c, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={tdSx}><Rank n={i + 1} /></TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#152346' }}>{c.name}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#374151' }}>{c.count}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 700, color: '#1565c0', fontSize: 14 }}>{fmtINR(c.revenue)}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#374151' }}>{fmtINR(c.revenue / c.count)}</TableCell>
                  <TableCell sx={tdSx}><ShareBar pct={totalRevenue > 0 ? (c.revenue / totalRevenue) * 100 : 0} color="#00897b" /></TableCell>
                </TableRow>
              ))}
              {topCustomers.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, color: '#9ca3af', fontSize: 14 }}>No data in selected range</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ── By Product ── */}
      {activeReport === 'products' && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionHeader
            title={`${rv} by Product`}
            sub={`Products ranked by ${rvLow} in the selected period`}
          />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>#</TableCell>
                <TableCell sx={thSx}>Product</TableCell>
                <TableCell sx={thSx}>SKU</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>{isDistributor ? 'Qty Sold' : 'Qty Ordered'}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>{rv}</TableCell>
                <TableCell sx={{ ...thSx, minWidth: 160 }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topProducts.map((p, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={tdSx}><Rank n={i + 1} /></TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#152346', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                  <TableCell sx={{ ...tdSx, color: '#6b7280', fontSize: 12 }}>{p.sku}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#374151' }}>{p.qty.toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 700, color: '#6a1b9a', fontSize: 14 }}>{fmtINR(p.revenue)}</TableCell>
                  <TableCell sx={tdSx}><ShareBar pct={totalRevenue > 0 ? (p.revenue / totalRevenue) * 100 : 0} color="#6a1b9a" /></TableCell>
                </TableRow>
              ))}
              {topProducts.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, color: '#9ca3af', fontSize: 14 }}>No data in selected range</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ── Status Breakdown ── */}
      {activeReport === 'status' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {statusBreakdown.map(s => (
              <Grid item xs={6} sm={4} md={3} key={s.status}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `2px solid ${STATUS_COLOR[s.status] ?? '#9ca3af'}33` }}>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Chip label={s.status.replace(/_/g, ' ')} size="small"
                      sx={{ bgcolor: `${STATUS_COLOR[s.status] ?? '#9ca3af'}18`, color: STATUS_COLOR[s.status] ?? '#6b7280', fontWeight: 700, fontSize: 11, mb: 1.5 }} />
                    <Typography sx={{ fontSize: 32, fontWeight: 800, color: STATUS_COLOR[s.status] ?? '#374151', lineHeight: 1 }}>{s.count}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1 }}>orders</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#152346' }}>{fmtINR(s.revenue)}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{rvLow}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {statusBreakdown.length > 0 && (
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <SectionHeader title={`${rv} Distribution by Status`} />
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {statusBreakdown.map(s => {
                  const pct = totalRevenue > 0 ? (s.revenue / totalRevenue) * 100 : 0;
                  const c = STATUS_COLOR[s.status] ?? '#9ca3af';
                  return (
                    <Box key={s.status}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: c }}>{s.status.replace(/_/g, ' ')}</Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Typography sx={{ fontSize: 13, color: '#374151' }}>{s.count} orders</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', minWidth: 70, textAlign: 'right' }}>{fmtINR(s.revenue)}</Typography>
                          <Typography sx={{ fontSize: 13, color: '#6b7280', minWidth: 45, textAlign: 'right' }}>{pct.toFixed(1)}%</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ height: 14, borderRadius: 7, bgcolor: '#f3f4f6', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: c, borderRadius: 7, transition: 'width 0.4s' }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          )}
        </Box>
      )}

      {/* ── Daily Trend ── */}
      {activeReport === 'trend' && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionHeader title={`Daily ${rv} Trend`} sub={`${rv} by day over selected period`} />
          <Box sx={{ p: 2 }}>
            <BarChart data={dailyTrend} color="#c62828" />
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', mb: 1 }}>Top {rv} Days</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Date</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{rv}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Orders</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyTrend.filter(d => d.value > 0)
                  .map(d => ({ ...d, orderCount: filtered.filter(o => { const od = new Date(o.orderDate); return od.toDateString() === d.date.toDateString(); }).length }))
                  .sort((a, b) => b.value - a.value).slice(0, 15)
                  .map((d, i) => (
                    <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                      <TableCell sx={tdSx}>{d.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 700, color: '#c62828', fontSize: 14 }}>{fmtINR(d.value)}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{d.orderCount}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* ── Fulfilment Report ── */}
      {activeReport === 'reps' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {[
              { label: 'Total Rep Orders', value: String(repOrderRows.reduce((s, r) => s + r.orders.length, 0)), color: '#7B61FF' },
              { label: 'Reps Active', value: String(repOrderRows.length), color: '#00897b' },
              { label: 'Total Value', value: fmtINRFull(repOrderRows.reduce((s, r) => s + r.orders.reduce((ss, o) => ss + orderValue(o), 0), 0)), color: '#1565c0' },
            ].map(m => (
              <Grid item xs={6} md={4} key={m.label}>
                <MetricCard label={m.label} value={m.value} color={m.color} />
              </Grid>
            ))}
          </Grid>

          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <SectionHeader title="Orders by Sales Representative" />
            {repOrderRows.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: '#6b7280' }}>
                <Typography sx={{ fontSize: 13 }}>No rep orders found in this date range.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      {['', 'Rep Name', 'Orders', 'Total Value', 'Avg Order Value'].map(h => (
                        <TableCell key={h} sx={thSx}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repOrderRows.map(row => {
                      const totalVal = row.orders.reduce((s, o) => s + orderValue(o), 0);
                      const avgVal = row.orders.length > 0 ? totalVal / row.orders.length : 0;
                      const isExpanded = expandedRepIds.has(row.key);
                      return (
                        <>
                          <TableRow key={String(row.key)} hover sx={{ cursor: 'pointer' }}
                            onClick={() => setExpandedRepIds(prev => {
                              const next = new Set(prev);
                              isExpanded ? next.delete(row.key) : next.add(row.key);
                              return next;
                            })}>
                            <TableCell sx={{ width: 40 }}>
                              <IconButton size="small">
                                {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#7B61FF' }}>{row.name}</TableCell>
                            <TableCell sx={tdSx}>{row.orders.length}</TableCell>
                            <TableCell sx={{ ...tdSx, fontWeight: 700 }}>{fmtINRFull(totalVal)}</TableCell>
                            <TableCell sx={tdSx}>{fmtINRFull(avgVal)}</TableCell>
                          </TableRow>
                          <TableRow key={`${String(row.key)}-detail`}>
                            <TableCell colSpan={5} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ bgcolor: '#fafbff', px: 4, py: 1.5, overflowX: 'auto' }}>
                                  <Table size="small" sx={{ minWidth: 560 }}>
                                    <TableHead>
                                      <TableRow>
                                        {['Order #', 'Date', 'Retailer', 'Items', 'Value', 'Status'].map(h => (
                                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280', py: 0.75 }}>{h}</TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.orders.map(o => (
                                        <TableRow key={o.id}>
                                          <TableCell sx={{ fontSize: 12, color: '#1a56db', fontWeight: 600 }}>#{o.orderNumber ?? o.id}</TableCell>
                                          <TableCell sx={{ fontSize: 12 }}>{new Date(o.orderDate ?? o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                                          <TableCell sx={{ fontSize: 12 }}>{peerMap[o.retailerTenantId] ?? `#${o.retailerTenantId}`}</TableCell>
                                          <TableCell sx={{ fontSize: 12 }}>{o.items?.length ?? 0}</TableCell>
                                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{fmtINRFull(orderValue(o))}</TableCell>
                                          <TableCell>
                                            <Box component="span" sx={{ fontSize: 10, fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: `${STATUS_COLOR[o.status] ?? '#374151'}18`, color: STATUS_COLOR[o.status] ?? '#374151' }}>
                                              {o.status}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Box>
      )}

      {activeReport === 'delivery' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {[
              { label: 'Total Orders', value: fulfilmentStats.total, color: '#374151' },
              { label: 'Delivered / Invoiced', value: fulfilmentStats.delivered, color: '#2e7d32' },
              { label: 'In Progress', value: fulfilmentStats.confirmed, color: '#0277bd' },
              { label: 'Pending Review', value: fulfilmentStats.pending, color: '#ef6c00' },
              { label: 'Cancelled / Rejected', value: fulfilmentStats.cancelled, color: '#c62828' },
            ].map(m => (
              <Grid item xs={6} md={2.4} key={m.label}>
                <MetricCard label={m.label} value={String(m.value)} color={m.color}
                  sub={fulfilmentStats.total > 0 ? `${((m.value / fulfilmentStats.total) * 100).toFixed(1)}% of total` : undefined} />
              </Grid>
            ))}
          </Grid>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <SectionHeader title="Fulfilment Breakdown" />
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Delivered / Invoiced', value: fulfilmentStats.delivered, color: '#2e7d32' },
                { label: 'In Progress (Confirmed / Dispatched)', value: fulfilmentStats.confirmed, color: '#0277bd' },
                { label: 'Pending Review (Placed / Viewed)', value: fulfilmentStats.pending, color: '#ef6c00' },
                { label: 'Cancelled / Rejected', value: fulfilmentStats.cancelled, color: '#c62828' },
              ].map(r => {
                const pct = fulfilmentStats.total > 0 ? (r.value / fulfilmentStats.total) * 100 : 0;
                return (
                  <Box key={r.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: r.color }}>{r.label}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346' }}>{r.value} <span style={{ color: '#6b7280', fontWeight: 400 }}>({pct.toFixed(1)}%)</span></Typography>
                    </Box>
                    <Box sx={{ height: 16, borderRadius: 8, bgcolor: '#f3f4f6', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: r.color, borderRadius: 8, transition: 'width 0.4s' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Box>
      )}
    </Box>
  );
}
